import { createDiscoverySdk } from '@atlassian/discovery-sdk';
import { standardizeLocale } from '@atlassian/jira-common-constants/src/supported-locales';
import { ff } from '@atlassian/jira-feature-flagging-using-meta';
import getMeta from '@atlassian/jira-get-meta';
import { useProject } from '@atlassian/jira-navigation-apps-sidebar-common';
import { useOrgId } from '@atlassian/jira-router-resources-navigation-org-id';
import { useWorkspaceContext } from '@atlassian/jira-servicedesk-insight-common-workspace-context';
import type { Locale } from '@atlassian/jira-shared-types';
import { useTenantContext } from '@atlassian/jira-tenant-context-controller';

const getDiscoverySurfaceCohortValue = (locale: Locale) => {
	if (locale.startsWith('en')) {
		return ff('jira.frontend.atlassian-switcher.discovery-sdk');
	}
	return ff('jira.app-recommendations.discovery-sdk.all-locale');
};

// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
const usersLocale = (getMeta('ajs-user-locale') ?? '') as unknown as Locale;
const { useDiscoverySdk, DiscoverySurface } = createDiscoverySdk({
	context: {
		product: 'jira',
	},
	enable: getDiscoverySurfaceCohortValue(usersLocale),
});

export const DiscoverySurfaceContextCollector = ({ isOpen }: { isOpen: boolean }) => {
	const { cloudId, isSiteAdmin } = useTenantContext();
	const { data: orgId } = useOrgId();
	const { data } = useProject();
	const { workspaceId } = useWorkspaceContext();
	const { setContext } = useDiscoverySdk();

	setContext((current) => ({
		...current,
		tenantId: cloudId || undefined,
		subproduct: data?.type,
		locale: standardizeLocale(usersLocale),
		isSiteAdmin,
		// stays true after the switcher has been opened for the first time
		switcherMenuHasBeenOpened: current.switcherMenuHasBeenOpened ? true : isOpen,
		orgId,
		workspaceId,
	}));

	return null;
};

export { DiscoverySurface };
